import type { MutableRefObject, RefObject } from 'react';

import { useCallback, useEffect, useRef, useState } from 'react';

interface useElementResizeObserverValue {
  /** Height of the target element's border box. */
  height: number;
  /** Reference to the target element being observed. */
  target: Element;
  /** Width of the target element's border box. */
  width: number;
}

/**
 * Uses a ResizeObserver to detect changes to an element's border box or content.
 * Accepts a callback that will be called any time the element resize occurs.
 */
function useElementResizeObserver(targetElement: Element | MutableRefObject<Element> | RefObject<Element> | null) {
  const observerRef = useRef<ResizeObserver>();
  const [value, setValue] = useState<useElementResizeObserverValue | null>(null);

  /**
   * Callback deferred to the next animation frame using `requestAnimationFrame`
   * order to prevent `ResizeObserver loop completed with undelivered
   * notifications.` errors. This happens when a consuming component reacts to a
   * resize event and updates layout styles that then triggers another resize
   * event to occur. Deferring the callback to the next animation frame ensures
   * that all resize events are processed before the next layout update.
   * @link https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_errors
   */
  const handleResize = useCallback(([entry]: ResizeObserverEntry[]) => {
    requestAnimationFrame(() => {
      setValue({
        height: entry.borderBoxSize[0].blockSize,
        target: entry.target,
        width: entry.borderBoxSize[0].inlineSize,
      });
    });
  }, []);

  // When element changes, unobserve all previous targets before observing the
  // new target.
  useEffect(() => {
    const element = targetElement instanceof Element ? targetElement : targetElement?.current;
    observerRef.current?.disconnect();
    observerRef.current = new ResizeObserver(handleResize);

    if (element) {
      observerRef.current?.observe(element);
    }

    return () => observerRef.current?.disconnect();
  }, [handleResize, targetElement]);

  return value;
}

export default useElementResizeObserver;
